package com.xtream.ui.tv.presentation.search

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.paging.LoadState
import androidx.paging.compose.LazyPagingItems
import androidx.paging.compose.collectAsLazyPagingItems
import androidx.paging.compose.itemContentType
import androidx.paging.compose.itemKey
import com.xtream.composeapp.LocalAppViewModelStoreOwner
import com.xtream.composeapp.data.Screen
import com.xtream.composeapp.presentation.ui.navigation.RootNavViewModel
import com.xtream.composeapp.presentation.ui.search.FilterTab
import com.xtream.composeapp.presentation.ui.search.MoviesFilterPane
import com.xtream.composeapp.presentation.ui.search.SearchScreenViewModel
import com.xtream.composeapp.presentation.ui.search.SeriesFilterPane
import com.xtream.composeapp.presentation.ui.search.TvFilterPane
import com.xtream.sharedResources.resources.Res
import com.xtream.sharedResources.resources.clapper_open
import com.xtream.sharedResources.resources.home_2
import com.xtream.sharedResources.resources.live
import com.xtream.sharedResources.resources.movies
import com.xtream.sharedResources.resources.screen_ic
import com.xtream.sharedResources.resources.search_hint
import com.xtream.sharedResources.resources.series
import com.xtream.data_kmp.dto.BaseStream
import com.xtream.data_kmp.dto.LiveStream
import com.xtream.data_kmp.dto.SeriesStream
import com.xtream.data_kmp.dto.VodStream
import com.xtream.ui.tv.presentation.common.rememberRowFocusState
import com.xtream.ui.ui.layout.TvColumnConfig
import com.xtream.ui.ui.layout.TvColumnHeaderMode
import com.xtream.ui.ui.layout.TvContentScreen
import com.xtream.ui.ui.util.extensions.focusAlphaFade
import com.xtream.ui.ui.widget.FocusPositioner
import com.xtream.ui.ui.widget.InTvTextEdit
import com.xtream.ui.ui.widget.LazyContentRow
import com.xtream.ui.ui.widget.NetworkImageCard
import com.xtream.ui.ui.widget.SideDialog
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

fun Modifier.debugFocus(tag: String): Modifier = this.onFocusChanged {
    if (it.isFocused||it.hasFocus) {
     }
}
@Composable
fun TvSearchScreen(
    navKey: Screen.Search,
    rowsContentPadding: PaddingValues,
    contentPadding: PaddingValues
) {
    val viewModelStoreOwner =LocalAppViewModelStoreOwner.current// activity as ViewModelStoreOwner

    val rootNavViewModel: RootNavViewModel = koinViewModel(viewModelStoreOwner = viewModelStoreOwner)

    // Replace SearchViewModel with SearchScreenViewModel
    val searchViewModel: SearchScreenViewModel = koinViewModel(
        key = "searchStream_${navKey::class.simpleName}${navKey.hashCode()}"
    )
    val searchQuery by searchViewModel.searchQuery
        .collectAsStateWithLifecycle("")
    val liveStreamsPaging = searchViewModel.liveStreamsFilteredPagingFlow.collectAsLazyPagingItems()
    val vodStreamsPaging = searchViewModel.moviesFilteredPagingFlow.collectAsLazyPagingItems()
    val seriesStreamsPaging = searchViewModel.seriesFilteredPagingFlow.collectAsLazyPagingItems()


    val liveStreamsListState = rememberLazyListState()
    val vodStreamsListState = rememberLazyListState()
    val seriesStreamsListState = rememberLazyListState()

    val columnFocusRequester = remember { FocusRequester() }
    val defaultRowFocusRequester = remember { FocusRequester() }

    fun shouldShowRow(pagingItems: LazyPagingItems<out Any>) =
        pagingItems.itemCount > 0 || pagingItems.loadState.refresh is LoadState.Loading

    // ✅ Stable string keys — row identity survives other rows appearing/disappearing
    val visibleRows by remember {
        derivedStateOf {
            buildList {
                if (shouldShowRow(liveStreamsPaging)) add("live")
                if (shouldShowRow(vodStreamsPaging)) add("vod")
                if (shouldShowRow(seriesStreamsPaging)) add("series")
            }
        }
    }

    val layoutDirection = LocalLayoutDirection.current

    val density = LocalDensity.current
    val screenWidthDp = with(density) {
        LocalWindowInfo.current.containerSize.width.toDp()
    }
    val startPadding = rowsContentPadding.calculateStartPadding( layoutDirection)

    val parentFraction = remember(screenWidthDp) {
        startPadding.value / screenWidthDp.value
    }
    var showFilters by remember { mutableStateOf(false) }

    TvContentScreen(
        items = visibleRows,

        columnFocusRequester = columnFocusRequester,
        defaultRowFocusRequester = defaultRowFocusRequester,

        // ✅ Stable key from the string value itself, not its index
        itemKey = { _, rowKey -> rowKey },
        config = TvColumnConfig(
            headerMode = TvColumnHeaderMode.AutoHide,

            headerPadding = PaddingValues(top = 24.dp, bottom = 24.dp, end = 100.dp)

        ),
        header = {
            Row(modifier = Modifier.fillMaxWidth()) {

                InTvTextEdit(
                    value = searchQuery,
                    leadingIcon = Icons.Filled.Search,
                    onValueChange = { searchViewModel.onQueryChanged(it) },
                    hint = stringResource(Res.string.search_hint),
                    modifier = Modifier
                        .height(40.dp)
                        .weight(1f)
                        .padding(rowsContentPadding)
                        .background(
                            MaterialTheme.colorScheme.onBackground.copy(alpha = .08f),
                            RoundedCornerShape(8.dp)
                        ),
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                    textStyle = MaterialTheme.typography.titleMedium,
                )
                IconButton(onClick = { showFilters = true }) {
                    Icon(Icons.Default.Tune, contentDescription = "Filters")
                }      }

        },
        itemRow = { _, rowKey, rowFocusRequester ->
            when (rowKey) {

                "live" -> SearchRow(
                    title = stringResource(Res.string.live),
                    parentFraction= parentFraction,
                    pagingItems = liveStreamsPaging,
                    listState = liveStreamsListState,
                    rootNavViewModel = rootNavViewModel,
                    focusRequester = rowFocusRequester,
                    contentPadding = PaddingValues( start = startPadding, end=screenWidthDp),

                )
                "vod" -> SearchRow(
                    title = stringResource(Res.string.movies),
                    parentFraction= parentFraction,

                    pagingItems = vodStreamsPaging,
                    listState = vodStreamsListState,
                    rootNavViewModel = rootNavViewModel,
                    focusRequester = rowFocusRequester,
                    contentPadding = PaddingValues( start = startPadding, end=screenWidthDp),
                )
                "series" -> SearchRow(
                    title = stringResource(Res.string.series),
                    parentFraction= parentFraction,

                    pagingItems = seriesStreamsPaging,
                    listState = seriesStreamsListState,
                    rootNavViewModel = rootNavViewModel,
                    focusRequester = rowFocusRequester,
                    contentPadding = PaddingValues( start = startPadding, end=screenWidthDp),
                )
            }
        }
    )


// Add a filter button to your existing TvSearchScreen header or as an action
// Then add the SideDialog at the bottom of the composable:

    SideDialog(
        show = showFilters,
        onDismiss = { showFilters = false },
        title = "Filters",
    ) {
        TvFiltersContent(
            searchScreenViewModel = searchViewModel,
            activeTab = when (visibleRows.firstOrNull()) {
                "vod"    -> FilterTab.MOVIES
                "series" -> FilterTab.SERIES
                else     -> FilterTab.TV
            },
        )
    }


    LaunchedEffect(searchQuery){
        liveStreamsListState  .scrollToItem(0)
        vodStreamsListState   .scrollToItem(0)
        seriesStreamsListState.scrollToItem(0)
    }
}
@Composable
fun TvFiltersContent(
    searchScreenViewModel: SearchScreenViewModel,
    activeTab: FilterTab = FilterTab.TV,
) {
    val filterState by searchScreenViewModel.uiConfigState.collectAsStateWithLifecycle()
    var selectedTab by remember { mutableStateOf(activeTab) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(bottom = 16.dp),
    ) {
        // Tab row
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 8.dp),
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            FilterTab.entries.forEach { tab ->
                val selected = tab == selectedTab
                val (painter, label) = when (tab) {
                    FilterTab.TV     -> painterResource(Res.drawable.home_2)       to "TV"
                    FilterTab.MOVIES -> painterResource(Res.drawable.clapper_open) to "Movies"
                    FilterTab.SERIES -> painterResource(Res.drawable.screen_ic)    to "Series"
                }
                Surface(
                    onClick = { selectedTab = tab },
                    modifier = Modifier.weight(1f),
                    shape = CircleShape,
                    color = if (selected) MaterialTheme.colorScheme.primary
                    else MaterialTheme.colorScheme.surfaceVariant,
                    contentColor = if (selected) MaterialTheme.colorScheme.onPrimary
                    else MaterialTheme.colorScheme.onSurfaceVariant,
                ) {
                    Row(
                        modifier = Modifier.padding(vertical = 6.dp),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Icon(painter, contentDescription = null, modifier = Modifier.size(14.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(label, style = MaterialTheme.typography.labelSmall)
                    }
                }
            }
        }

        HorizontalDivider(thickness = 0.5.dp)

        // Pane content
        when (selectedTab) {
            FilterTab.TV -> TvFilterPane(
                state = filterState.liveTvConfig,
                searchScreenViewModel = searchScreenViewModel,
                onUpdate = { newState -> searchScreenViewModel.updateLiveConfig { newState } },
            )
            FilterTab.MOVIES -> MoviesFilterPane(
                state = filterState.movieConfig,
                searchScreenViewModel = searchScreenViewModel,
                onUpdate = { newState -> searchScreenViewModel.updateMovieConfig { newState } },
            )
            FilterTab.SERIES -> SeriesFilterPane(
                state = filterState.seriesConfig,
                searchScreenViewModel = searchScreenViewModel,
                onUpdate = { newState -> searchScreenViewModel.updateSeriesConfig { newState } },
            )
        }

        // Reset button at bottom
        TextButton(
            onClick = { searchScreenViewModel.resetUiState() },
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),
        ) {
            Text("Reset all", style = MaterialTheme.typography.labelMedium)
        }
    }
}
// Extract the Row to keep the main screen clean and stable
// ─── SearchRow — remove unused focusRestorer param ─────────────────────────

@Composable
fun SearchRow(
    title: String,
    pagingItems: LazyPagingItems<out BaseStream>,
    modifier: Modifier = Modifier,
    parentFraction: Float=.5f,
    listState: LazyListState = rememberLazyListState(),
    rootNavViewModel: RootNavViewModel,
    focusRequester: FocusRequester = FocusRequester.Default,
    // ✅ Removed focusRestorer param — RowFocusState handles it internally
    contentPadding: PaddingValues = PaddingValues(horizontal = 16.dp),
    actionTitle: String? = null,
    onActionClick: (() -> Unit)? = null,
) {
    val focusState = rememberRowFocusState()

    FocusPositioner(
        parentFraction = parentFraction,
        childFraction = 0f,
    ) {
        LazyContentRow(
            title = title,
            focusRequester = focusRequester,
            focusRestorer = focusState.currentRestorer, // read during composition ✅
            contentPadding = contentPadding,
            actionTitle = actionTitle,
            onActionClick = onActionClick,
            state = listState,
            modifier = modifier,
        ) {

            items(
                count = pagingItems.itemCount,
                key = pagingItems.itemKey { it.streamId },
                contentType = pagingItems.itemContentType { it::class.simpleName }
            ) { index ->
                val stream = pagingItems[index]
                if (stream != null) {
                    NetworkImageCard(
                        modifier = focusState
                            .itemModifier(index)
                            .focusAlphaFade(
                                itemIndex = index,
                                lastFocusedIndex = { focusState.lastFocusedItem },
                            ), // ✅
                        src = stream.streamIcon,
                        title = stream.name ?: "",
                        aspectRatio = if (stream is LiveStream) 1f else 2 / 3f,
                        onClick = {
                            when (stream) {
                                is LiveStream -> rootNavViewModel.navigate(Screen.LiveStreamPlayer(stream.streamId, stream.categoryId, stream.name))
                                is VodStream -> rootNavViewModel.navigate(Screen.MovieDetail(stream.streamId))
                                is SeriesStream -> rootNavViewModel.navigate(Screen.TvShowDetail(stream.streamId))
                            }
                        }
                    )
                }
            }
        }
    }
}


// ─── TvSearchScreen — add row focus tracking ───────────────────────

/*

@Composable
fun TvSearchScreen(
navKey: Screen.Search,
rowsContentPadding: PaddingValues,
contentPadding: PaddingValues
){
val activity = LocalActivity.current as ViewModelStoreOwner
val rootNavViewModel: RootNavViewModel = koinViewModel(
    viewModelStoreOwner= activity
)
val searchViewModel: SearchViewModel = koinViewModel( key="vodStream_${navKey::class.simpleName}${navKey.hashCode()}")
val searchFlow by searchViewModel.searchContents.collectAsStateWithLifecycle(null)

val list = remember(searchFlow) {
    val processedList = searchFlow?.toList() ?: emptyList()
    // This will only log when the MAP actually updates
    Log.d("TvSearchScreen", "Data Reloaded - Size: ${processedList.size}, Hash: ${processedList.hashCode()}")
    processedList
}
val searchQuery by searchViewModel.searchFlow.collectAsStateWithLifecycle()


val streamControlViewModel:StreamControlViewModel = koinViewModel(viewModelStoreOwner=activity)
val liveStreamsPaging = remember(streamControlViewModel) {
    streamControlViewModel.liveStreamsFilteredPagingFlow
}.collectAsLazyPagingItems()

val vodStreamsViewModel: VodStreamsViewModel = koinViewModel( key="vodStream_${navKey::class.simpleName}${navKey.hashCode()}")*/
/* hiltViewModel<VodStreamsViewModel, VodStreamsViewModel.Factory>(
    creationCallback = { factory ->
        factory.create(navKey)
    }
)*//*

val vodStreamsPaging = vodStreamsViewModel.vodStreamsFilteredPagingFlow.collectAsLazyPagingItems()

val seriesStreamsViewModel: SeriesViewModel = koinViewModel( key="seriesStream_${navKey::class.simpleName}${navKey.hashCode()}")*/
/* hiltViewModel<SeriesViewModel, SeriesViewModel.Factory>(
    creationCallback = { factory ->
        factory.create(navKey)
    }
)*//*

val seriesStreamsPaging = seriesStreamsViewModel.seriesStreamsFilteredPagingFlow.collectAsLazyPagingItems()

val columnListState = rememberLazyListState()
val liveStreamsListState = rememberLazyListState()
val vodStreamsListState = rememberLazyListState()
val seriesStreamsListState = rememberLazyListState()

val isLoadingItems = liveStreamsPaging.loadState.refresh is androidx.paging.LoadState.Loading
        || vodStreamsPaging.loadState.refresh is androidx.paging.LoadState.Loading
        || seriesStreamsPaging.loadState.refresh is androidx.paging.LoadState.Loading
val noResultFound = liveStreamsPaging.itemCount==0&& vodStreamsPaging.itemCount==0&& seriesStreamsPaging.itemCount==0

var shouldResetScroll by remember { mutableStateOf(false) }




val columnFocusRequester = remember { FocusRequester() }
val liveRowFocusRequester = remember { FocusRequester() }
val vodRowFocusRequester = remember { FocusRequester() }
val seriesRowFocusRequester = remember { FocusRequester() }


fun getDefaultRowFocusRequester():FocusRequester?{
    return when {
        liveStreamsPaging.itemCount != 0 -> liveRowFocusRequester
        vodStreamsPaging.itemCount != 0 -> vodRowFocusRequester
        seriesStreamsPaging.itemCount != 0 -> seriesRowFocusRequester
        else -> {
           null
        }
    }
}
 @OptIn(ExperimentalMaterial3Api::class)

  val scrollBehavior = TopAppBarDefaults.exitUntilCollapsedScrollBehavior(rememberTopAppBarState())


@OptIn(ExperimentalMaterial3Api::class)
Scaffold(
modifier = Modifier.nestedScroll(scrollBehavior.nestedScrollConnection)
,
    topBar = {
        CenterAlignedTopAppBar(
            scrollBehavior = scrollBehavior,
            modifier = Modifier
                .padding(top = contentPadding.calculateTopPadding()),
            title = {
                InTvTextEdit(
                    value = searchQuery,
                    leadingIcon = Icons.Filled.Search,
                    onValueChange = { searchViewModel.search( it) },
                    hint = stringResource(Res.string.search_hint),
                    modifier = Modifier
                        .height(40.dp) // Adjust height
                        .fillMaxWidth()
                        .onFocusChanged {
                            shouldResetScroll = it.isFocused
                        }

                        .padding(rowsContentPadding)
                        .background(
                            MaterialTheme.colorScheme.onBackground.copy(alpha = .08f),
                            RoundedCornerShape(8.dp)
                        ), // Background with rounded corners
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                    textStyle = MaterialTheme.typography.titleMedium,

                    )
            }
        )
    }
) {innerPadding->

       // modifier = Modifier.padding(top = innerPadding.calculateTopPadding()),

*/
/*

    if (!isLoadingItems && noResultFound && searchQuery.isNotEmpty())
        Column ( modifier = Modifier
            .fillMaxSize()
            .padding(top = innerPadding.calculateTopPadding()+contentPadding.calculateTopPadding()),

            verticalArrangement = Arrangement.Center,
            horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally
            ) {
            Icon(
                Icons.Filled.SearchOff,
                contentDescription = stringResource(Res.string.no_results),
                modifier = Modifier
                    .size(92.dp)
                    .padding(bottom = 8.dp)
            )
            Text(
                stringResource(Res.string.no_results), style = MaterialTheme.typography.titleLarge,
            )
        }

    else
*//*


        LazyColumn(
            state = columnListState,
            modifier = Modifier
                .fillMaxHeight()
                //   .focusGroup()
                .focusRequester(columnFocusRequester)
               // .focusRestorer(defaultRowFocusRequester)
            ,
            contentPadding = PaddingValues(top= 4.dp, bottom =48.dp ),

            ) {


            itemsIndexed(
                list,
                key = { _, pair -> pair.first})
            { rowIndex, pair ->
                val pagingData = (pair.second as Flow<PagingData<BaseStream>>).collectAsLazyPagingItems()
                val defaultRowItemFocusRequest = remember { FocusRequester() }
                LazyContentRow(
                    state = liveStreamsListState,
                    contentPadding = rowsContentPadding,
                    focusRequester = liveRowFocusRequester,
                    focusRestorer  = defaultRowItemFocusRequest,

                    title = stringResource(pair.first)
                ) {

                items(
                        pagingData.itemCount,
                        key = pagingData.itemKey { it -> it.streamId?: it.hashCode()},
                        contentType = {
                            pagingData.itemContentType { pair.second }
                        }
                    ) {index->
                    val modifier = if (index == 0)
                        Modifier.focusRequester(defaultRowItemFocusRequest)
                    else
                        Modifier
                    val stream = pagingData[index]?:return@items
Log.d("TvSearchScreen", "stream: $stream")
                    NetworkImageCard(
                        modifier = modifier,
                        src=stream.streamIcon,
                        title = stream.name?:"",
                        width = 100.dp,

                        //description = stream.name?:"",
                        onClick = {
                            //  columnFocusRequester.saveFocusedChild()
                            // liveRowFocusRequester.saveFocusedChild()

                            rootNavViewModel.navigate(LiveStreamPlayer(stream.streamId, stream.categoryId, stream.name))
                        },
                        aspectRatio = 1f
                    )

                }
                }

            }}


        */
/*
    LazyColumn(
        state = columnListState,
        modifier = Modifier
           .padding(top = innerPadding.calculateTopPadding())

              .then(
                getDefaultRowFocusRequester()?.let {
                    Modifier.focusRestorer(it)
                }
                    ?: Modifier
            ),
       // contentPadding = PaddingValues(top=innerPadding.calculateTopPadding()+8.dp, bottom = contentPadding.calculateBottomPadding(), )
    ) {


        // Live Streams Row
      if(liveStreamsPaging.itemCount >0)
            item(
                contentType = "LiveStreams",
                key = "live_streams_section",

                ) {
                val defaultRowItemFocusRequest = remember { FocusRequester() }
                LazyContentRow(
                    state = liveStreamsListState,
                    contentPadding = rowsContentPadding,
                    focusRequester = liveRowFocusRequester,
                    focusRestorer  = defaultRowItemFocusRequest,

                    title = stringResource(Res.string.tv_channels)
                ) {

                    items  (
                         liveStreamsPaging.itemCount,
                         key =  liveStreamsPaging.itemKey { it -> it.streamId } ,
                         // state = liveStreamsListState // Add this if you want to control scrolling for this specific row

                     ) {index->
                         val stream = liveStreamsPaging[index]?:return@items

                         val modifier = if (index == 0)
                                 Modifier.focusRequester(defaultRowItemFocusRequest)
                         else
                             Modifier

                         NetworkImageCard(
                             modifier = modifier,
                             src=stream.streamIcon,
                             title = stream.name?:"",
                             width = 100.dp,

                             //description = stream.name?:"",
                             onClick = {
                              //  columnFocusRequester.saveFocusedChild()
                              // liveRowFocusRequester.saveFocusedChild()

                                 rootNavViewModel.navigate(LiveStreamPlayer(stream.streamId, stream.categoryId, stream.name))
                             },
                             aspectRatio = 1f
                         )

                     }

                }
            }

        // VOD Streams Row
        if (vodStreamsPaging.itemCount >0)
        item(
            contentType = "Movies",
            key = "movies_section"
            ){
            val defaultRowItemFocusRequest = remember { FocusRequester() }

            LazyContentRow(
                state =  vodStreamsListState,
                contentPadding = rowsContentPadding,
                focusRequester = vodRowFocusRequester,
                focusRestorer  = defaultRowItemFocusRequest,
                 title = stringResource(Res.string.movies)
            ) {

               items(
                   vodStreamsPaging.itemCount,
                   key =  vodStreamsPaging.itemKey { it -> it.streamId } ,
                   // state = vodStreamsListState // Add this if you want to control scrolling for this specific row
               ) {  index->
                   val stream = vodStreamsPaging[index]?:return@items

                   val modifier = if (index == 0)
                       Modifier.focusRequester(defaultRowItemFocusRequest)
                   else
                       Modifier


                   NetworkImageCard(
                       modifier = modifier,
                       src=stream.streamIcon,
                       title = stream.name?:"",
                       width = 100.dp,

                       description = stream.formattedRating()?:"",
                       onClick = {
                         // columnFocusRequester.saveFocusedChild()
                         // vodRowFocusRequester.saveFocusedChild()

                           rootNavViewModel.navigate(Screen.MovieDetail(stream.streamId))
                       }
                   )

               }

            }
        }

        // Series Streams Row
        if ( seriesStreamsPaging.itemCount >0)
        item(
            contentType = "TvShows",
            key = "shows_section"
        ){
            val defaultRowItemFocusRequest = remember { FocusRequester() }

            LazyContentRow(
                state = seriesStreamsListState,
                contentPadding = rowsContentPadding,
                focusRequester = seriesRowFocusRequester,
                focusRestorer  = defaultRowItemFocusRequest,


                title = stringResource(Res.string.tv_shows)
            ) {
                items(
                    seriesStreamsPaging.itemCount,
                    key =  seriesStreamsPaging.itemKey { it -> it.streamId } ,
                    // state = seriesStreamsListState // Add this if you want to control scrolling for this specific row
                ) { index ->
                    val stream = seriesStreamsPaging[index]?:return@items

                    val modifier = if (index == 0)
                        Modifier.focusRequester(defaultRowItemFocusRequest)
                    else
                        Modifier


                    NetworkImageCard(
                        modifier = modifier,
                        src=stream.streamIcon,
                        title = stream.name?:"",
                        description = stream.formattedRating()?:"",
                        onClick = {
                          // columnFocusRequester.saveFocusedChild()
                          // seriesRowFocusRequester.saveFocusedChild()

                            rootNavViewModel.navigate(Screen.TvShowDetail(stream.streamId))
                        }
                    )
                }

            }
        }

    }*//*


}


LaunchedEffect(searchQuery) {
//searchViewModel.search(searchQuery)
  */
/*  snapshotFlow { searchQuery }
        .debounce(300) // Only emit after 300ms of no changes
        .distinctUntilChanged()
        .collectLatest { debouncedSearch ->
            streamControlViewModel.search( debouncedSearch)
            vodStreamsViewModel.search( debouncedSearch)
            seriesStreamsViewModel.search( debouncedSearch)
        }*//*

}



LaunchedEffect(liveStreamsPaging.itemCount,vodStreamsPaging.itemCount,seriesStreamsPaging.itemCount){

    if(shouldResetScroll) {
        Log.d("TvSearchScreen", "resetting scroll")
        columnListState.animateScrollToItem(0)
        liveStreamsListState.scrollToItem(0)
        vodStreamsListState.scrollToItem(0)
        seriesStreamsListState.scrollToItem(0)
    }
}

}*/



